// useOffboardingChecklist — the interaction model behind the offboarding drawer.
// Each account is actioned INDEPENDENTLY (mirroring the original portal's
// per-row flow): an account-removal row can be "Mark removed" (approve) or
// "Decline" (refuse, with a reason); an admin-transfer row can be "Transferred"
// or "Not required". Every action hits the single members endpoint with a
// one-element `accountIds` — there is deliberately NO whole-task "Refuse" (that
// would wrongly decline already-removed accounts). Lives as a hook (not inside
// the view) because the row actions and the drawer share this state.

import { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import { appDisplayName } from "modules/apps/tenants";

import {
  MF_TASKS,
  type MFOffboardingAccount,
  type MFOffboardingDetail,
  type MFOffboardingUpdate,
} from "state/members/tasks";
import { ACCOUNT_NOT_UPDATED } from "state/members/offboardingWrites";

import {
  outstandingCount,
  rowState,
  type OffGroupKey,
  type OffRowState,
} from "./offboardingRowState";

export type { OffGroupKey, OffRowState } from "./offboardingRowState";

export interface OffChecklistItem {
  accountId: string;
  appName: string;
  appImageUrl?: string | null;
  state: OffRowState;
}

export interface OffChecklistGroup {
  key: OffGroupKey;
  label: string;
  items: OffChecklistItem[];
  resolvedCount: number; // items no longer pending (done, declined or cancelled)
  total: number;
}

const GROUP_LABEL: Record<OffGroupKey, string> = {
  admin: "Admin transfer",
  removal: "Account removal",
};

export interface OffboardingChecklistController {
  groups: OffChecklistGroup[];
  hasAccounts: boolean;
  /** Accounts still incomplete (drives the footer summary). */
  remaining: number;
  /** Account id whose row action is in flight — drives a per-row spinner. */
  busyId: string | null;
  // Account-removal row actions.
  markRemoved: (accountId: string) => void;
  // Admin-transfer row actions.
  markAdminTransferred: (accountId: string) => void;
  markAdminNotRequired: (accountId: string) => void;
  // Per-account decline (refuse) sub-flow — one open row at a time.
  decliningId: string | null;
  declineReason: string;
  setDeclineReason: (v: string) => void;
  startDecline: (accountId: string) => void;
  cancelDecline: () => void;
  confirmDecline: () => void;
  /** A decline needs a reason of at least this many characters. */
  canConfirmDecline: boolean;
}

export const useOffboardingChecklist = (
  detail: MFOffboardingDetail | undefined,
): OffboardingChecklistController => {
  const toast = useToast();
  const { update } = MF_TASKS.useUpdateOffboardingAccounts(detail?.offboardingId);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  // Reset transient state whenever the underlying task changes.
  useEffect(() => {
    setBusyId(null);
    setDecliningId(null);
    setDeclineReason("");
  }, [detail?.offboardingId]);

  const accounts = detail?.accounts ?? [];

  const appName = (accountId: string): string => {
    const account = accounts.find((a) => a.offboardingAccountId === accountId);
    return account ? appDisplayName(account.app) : "account";
  };

  // Fire one per-account update. Errors surface via the api layer's toast.
  const act = (
    accountId: string,
    body: Omit<MFOffboardingUpdate, "accountIds">,
    success: Parameters<typeof toast>[0],
  ) => {
    if (busyId) return;
    setBusyId(accountId);
    update({ accountIds: [accountId], ...body })
      .then((written) => toast(written ? success : ACCOUNT_NOT_UPDATED))
      .catch(() => undefined)
      .finally(() => setBusyId(null));
  };

  const markRemoved = (accountId: string) =>
    act(
      accountId,
      { markRemoved: true },
      {
        status: "success",
        title: "Account removed",
        description: `You confirmed ${appName(accountId)} access was removed.`,
      },
    );

  const markAdminTransferred = (accountId: string) =>
    act(
      accountId,
      { markAdminRightsTransferred: true },
      {
        status: "success",
        title: "Admin transfer confirmed",
        description: `Admin rights handled for ${appName(accountId)}.`,
      },
    );

  const markAdminNotRequired = (accountId: string) =>
    act(
      accountId,
      { markAdminRightsTransferred: true, adminTransferNotRequired: true },
      {
        status: "success",
        title: "Marked not required",
        description: `No admin transfer needed for ${appName(accountId)}.`,
      },
    );

  const startDecline = (accountId: string) => {
    setDecliningId(accountId);
    setDeclineReason("");
  };

  const cancelDecline = () => {
    setDecliningId(null);
    setDeclineReason("");
  };

  const canConfirmDecline = declineReason.trim().length >= 3;

  const confirmDecline = () => {
    if (!decliningId || !canConfirmDecline) return;
    const id = decliningId;
    act(
      id,
      { markDeclined: true, declinedReason: declineReason.trim() },
      {
        status: "info",
        title: "Account declined",
        description: `Sent back to IT: ${appName(id)}.`,
      },
    );
    setDecliningId(null);
    setDeclineReason("");
  };

  const buildGroup = (key: OffGroupKey): OffChecklistGroup => {
    const items: OffChecklistItem[] = accounts.map((a) => ({
      accountId: a.offboardingAccountId,
      appName: appDisplayName(a.app),
      appImageUrl: a.app.imageUrl,
      state: rowState(key, a),
    }));
    return {
      key,
      label: GROUP_LABEL[key],
      items,
      total: items.length,
      resolvedCount: items.filter((i) => i.state !== "pending").length,
    };
  };

  return {
    // Account removal first — it's the primary action and the default tab (see
    // OffboardingView), so it must be the leftmost SubTab.
    groups: [buildGroup("removal"), buildGroup("admin")],
    hasAccounts: accounts.length > 0,
    remaining: outstandingCount(accounts),
    busyId,
    markRemoved,
    markAdminTransferred,
    markAdminNotRequired,
    decliningId,
    declineReason,
    setDeclineReason,
    startDecline,
    cancelDecline,
    confirmDecline,
    canConfirmDecline,
  };
};
